import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../api/axiosInstance";
import { logoutUser } from "./authSlice";

const getErrorMessage = (err, fallback) =>
  err.response?.data?.message || fallback;

// filters is optional: { name, industry, businessSegment, startDate,
// endDate, page, limit }. page/limit absent means "no pagination, give me
// everything" — NewOptimization's client picker and Dashboard's client
// filter dropdown both rely on that (see backend/utils/pagination.js).
export const fetchClients = createAsyncThunk(
  "clients/fetchClients",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const params = {};
      for (const key of ["name", "industry", "businessSegment", "startDate", "endDate", "page", "limit"]) {
        if (filters[key]) params[key] = filters[key];
      }
      const res = await api.get("/client", { params });
      return { clients: res.data.clients, pagination: res.data.pagination || null };
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, "Failed to load clients."));
    }
  }
);

export const createClient = createAsyncThunk(
  "clients/createClient",
  async (clientData, { rejectWithValue }) => {
    try {
      const res = await api.post("/client", clientData);
      return res.data.client;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, "Failed to create client."));
    }
  }
);

// payload: { id, data: { name, industry, businessSegment, website, ... } }
export const updateClient = createAsyncThunk(
  "clients/updateClient",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await api.put(`/client/${id}`, data);
      return res.data.client;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, "Failed to update client."));
    }
  }
);

export const deleteClient = createAsyncThunk(
  "clients/deleteClient",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/client/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, "Failed to delete client."));
    }
  }
);

const initialState = {
  clients: [],
  pagination: null,
  status: "idle",
  error: null,
};

const clientSlice = createSlice({
  name: "clients",
  initialState,
  reducers: {
    clearClientError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClients.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchClients.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.clients = action.payload.clients;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchClients.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      .addCase(createClient.fulfilled, (state, action) => {
        state.clients.push(action.payload);
        state.clients.sort((a, b) => a.name.localeCompare(b.name));
      })
      .addCase(createClient.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(updateClient.fulfilled, (state, action) => {
        const updated = action.payload;
        const idx = state.clients.findIndex((c) => c._id === updated._id);
        if (idx !== -1) {
          // keep the populated createdBy from the list (update response isn't populated)
          state.clients[idx] = { ...updated, createdBy: state.clients[idx].createdBy };
        }
        state.clients.sort((a, b) => a.name.localeCompare(b.name));
      })
      .addCase(updateClient.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(deleteClient.fulfilled, (state, action) => {
        state.clients = state.clients.filter((c) => c._id !== action.payload);
      })
      .addCase(deleteClient.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Wipe on logout — same reasoning as candidateSlice.
      .addCase(logoutUser.fulfilled, () => initialState);
  },
});

export const { clearClientError } = clientSlice.actions;
export default clientSlice.reducer;